import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTranslation } from "@/hooks/useTranslation";
import { trpc } from "@/lib/trpc";
import { sessionToTemplateData } from "../templateUtils";
import type { StrengthSession } from "../types";

export function ApplyTrainingTemplateDialog({
  clientId,
  onClose,
  onApplied,
}: {
  clientId: number;
  onClose: () => void;
  onApplied: () => void;
}) {
  const { t } = useTranslation();
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const templates = trpc.trainingTemplate.list.useQuery({ klientId: clientId });
  const apply = trpc.trainingTemplate.applyToSession.useMutation();
  const submit = async () => {
    if (!templateId || !date) return;
    try {
      await apply.mutateAsync({
        klientId: clientId,
        id: templateId,
        datum: date,
      });
      toast.success(t("strengthProgress:templates.applied"));
      onApplied();
      onClose();
    } catch {
      toast.error(t("strengthProgress:templates.applyFailed"));
    }
  };
  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("strengthProgress:templates.apply")}</DialogTitle>
          <DialogDescription>
            {t("strengthProgress:templates.applyDescription")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>{t("strengthProgress:templates.title")}</Label>
            <Select
              value={templateId?.toString() ?? ""}
              onValueChange={v => setTemplateId(Number(v))}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={t("strengthProgress:templates.choose")}
                />
              </SelectTrigger>
              <SelectContent>
                {(templates.data ?? []).map(template => (
                  <SelectItem key={template.id} value={String(template.id)}>
                    {template.nazov}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {templates.isError ? (
              <p role="alert">{t("strengthProgress:messages.loadFailed")}</p>
            ) : !templates.isLoading && !templates.data?.length ? (
              <p className="text-sm text-muted-foreground">
                {t("strengthProgress:templates.empty")}
              </p>
            ) : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor="apply-training-date">
              {t("strengthProgress:fields.date")}
            </Label>
            <Input
              id="apply-training-date"
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("common:cancel")}
          </Button>
          <Button
            disabled={!templateId || !date || apply.isPending}
            onClick={submit}
          >
            {t("strengthProgress:templates.apply")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SaveTrainingTemplateDialog({
  session,
  onClose,
}: {
  session: StrengthSession;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const create = trpc.trainingTemplate.create.useMutation();
  const utils = trpc.useUtils();
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!name.trim()) return;
    try {
      await create.mutateAsync({
        klientId: session.klientId,
        nazov: name.trim(),
        data: sessionToTemplateData(session),
      });
      await utils.trainingTemplate.list.invalidate();
      toast.success(t("strengthProgress:templates.saved"));
      onClose();
    } catch {
      toast.error(t("strengthProgress:templates.saveFailed"));
    }
  };
  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t("strengthProgress:templates.saveSession")}
          </DialogTitle>
          <DialogDescription>
            {t("strengthProgress:templates.saveDescription")}
          </DialogDescription>
        </DialogHeader>
        <form
          id="save-training-template"
          onSubmit={submit}
          className="space-y-2"
        >
          <Label htmlFor="save-training-template-name">
            {t("strengthProgress:templates.name")}
          </Label>
          <Input
            id="save-training-template-name"
            value={name}
            onChange={e => setName(e.target.value)}
            maxLength={120}
            required
          />
        </form>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("common:cancel")}
          </Button>
          <Button
            type="submit"
            form="save-training-template"
            disabled={!name.trim() || create.isPending}
          >
            {t("common:save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
